import { Menu, MenuButton, useDisclosure } from "@chakra-ui/react";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useRect } from "@nens/react/hooks";

export interface ContextMenuProps<T extends HTMLElement> {
  renderMenu: (() => JSX.Element) | null;
  children: (ref: MutableRefObject<T | null>) => JSX.Element | null;
  onOpen?: () => void;
}

// Render menu is required in ContextMenuInner otherwise just render children
interface ContextMenuInnerProps<T extends HTMLElement>
  extends Omit<ContextMenuProps<T>, "renderMenu"> {
  renderMenu: () => JSX.Element;
}

export function ContextMenu<T extends HTMLElement = HTMLElement>(props: ContextMenuProps<T>) {
  const ref = useRef<T>(null);
  if (props.renderMenu === null) {
    return props.children(ref);
  }

  return <ContextMenuInner<T> {...(props as ContextMenuInnerProps<T>)} />;
}

export function ContextMenuInner<T extends HTMLElement>(props: ContextMenuInnerProps<T>) {
  const { isOpen, onOpen: onOpenDisclosure, onClose } = useDisclosure();
  const targetRef = useRef<T>(null);
  const rect = useRect(targetRef, true);
  // Click offset relative to the target element's top-left corner
  const clickOffsetRef = useRef<[number, number]>([0, 0]);

  // Destructure props for stable references in hooks
  const { renderMenu, onOpen: onOpenProps } = props;

  const onOpen = useCallback(() => {
    onOpenDisclosure();
    onOpenProps?.();
  }, [onOpenProps, onOpenDisclosure]);

  // Attach contextmenu event only to the target element via effect
  useEffect(() => {
    const node = targetRef.current;
    if (!node) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
      const r = node.getBoundingClientRect();
      clickOffsetRef.current = [e.clientX - r.left, e.clientY - r.top];
      onOpen();
    };
    node.addEventListener("contextmenu", handler);
    return () => node.removeEventListener("contextmenu", handler);
  }, [onOpen]);

  // Render portal into specified container or document.body to avoid DOM nesting warnings
  const portalContainer = document.body;
  const menuLeft = rect.left + clickOffsetRef.current[0];
  const menuTop = rect.top + clickOffsetRef.current[1];

  return (
    <>
      {props.children(targetRef)}

      {isOpen &&
        createPortal(
          <Menu isOpen={isOpen} gutter={0} onClose={onClose}>
            <MenuButton
              w={0}
              h={0}
              p={0}
              opacity={0}
              pointerEvents="none"
              style={{
                position: "fixed",
                left: menuLeft,
                top: menuTop,
                cursor: "default",
              }}
            />
            {renderMenu()}
          </Menu>,
          portalContainer
        )}
    </>
  );
}
